import React, { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UploadCloud, FileSpreadsheet, AlertTriangle, CheckCircle2, Download, Plus, X, Package, Users, Sparkles } from "lucide-react";

const toCSV = (rows, headers) => {
  if (!rows.length) return "";
  const esc = (v) => {
    const s = v === undefined || v === null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [headers.join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))].join("\n");
};

const download = (name, text) => {
  const blob = new Blob([text], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
};

const FileDrop = ({ label, file, onFile, testid }) => (
  <label
    htmlFor={testid}
    className={`flex-1 border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition ${file ? "border-emerald-400 bg-emerald-50" : "border-gray-300 hover:border-[var(--brand)] hover:bg-gray-50"}`}
    data-testid={testid}
  >
    <input id={testid} type="file" accept=".csv" className="hidden" onChange={(e) => onFile(e.target.files?.[0] || null)} />
    <UploadCloud size={26} className={`mx-auto mb-2 ${file ? "text-emerald-600" : "text-gray-400"}`} />
    <div className="font-semibold text-sm">{label}</div>
    <div className="text-xs text-gray-500 mt-1">{file ? file.name : "Click to select .csv"}</div>
  </label>
);

const FieldToggles = ({ headers, visible, setVisible, extra = [] }) => (
  <div className="flex flex-wrap gap-2 mb-3">
    {[...headers, ...extra].map((h) => {
      const on = visible.includes(h);
      return (
        <button
          key={h}
          onClick={() => setVisible(on ? visible.filter((x) => x !== h) : [...visible, h])}
          className={`px-2.5 py-1 rounded-full text-xs border transition ${on ? "bg-[var(--brand)] text-white border-[var(--brand)]" : "bg-white text-gray-600 border-gray-300 hover:border-gray-500"}`}
          data-testid={`toggle-${h.replace(/\s+/g, "-")}`}
        >
          {on ? <X size={11} className="inline mr-1" /> : <Plus size={11} className="inline mr-1" />}{h}
        </button>
      );
    })}
  </div>
);

const PreviewTable = ({ rows, headers, testid }) => (
  <div className="overflow-x-auto border border-[var(--border)] rounded-lg" data-testid={testid}>
    <table className="w-full text-sm">
      <thead className="bg-gray-50 border-b border-[var(--border)]">
        <tr>{headers.map((h) => <th key={h} className="text-left px-3 py-2 font-semibold text-gray-600">{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.slice(0, 200).map((r, i) => (
          <tr key={i} className="border-b border-[var(--border)] last:border-0 hover:bg-gray-50">
            {headers.map((h) => <td key={h} className="px-3 py-2">{String(r[h] ?? "")}</td>)}
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={headers.length} className="px-3 py-6 text-center text-gray-400">No rows</td></tr>}
      </tbody>
    </table>
    {rows.length > 200 && <div className="px-3 py-2 text-xs text-gray-500">Showing first 200 of {rows.length}</div>}
  </div>
);

export default function ReconcilePage() {
  const [itemsFile, setItemsFile] = useState(null);
  const [txFile, setTxFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // per-section column visibility
  const [productCols, setProductCols] = useState(["sku", "name", "on_hand", "sold", "revenue"]);
  const [clientCols, setClientCols] = useState(["name", "company", "email", "phone", "hits", "revenue", "flag_review"]);
  const [orphanCols, setOrphanCols] = useState([]);
  const [itemsCols, setItemsCols] = useState([]);

  const runReconcile = async () => {
    setError(""); setResult(null);
    if (!itemsFile || !txFile) { setError("Please attach both CSVs."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("items_file", itemsFile);
      fd.append("tx_file", txFile);
      const { data } = await api.post("/reconcile/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      setItemsCols(data.items.headers);
      const orphanHeaders = data.new_products.length ? Object.keys(data.new_products[0].source_row || {}) : [];
      setOrphanCols(orphanHeaders);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Reconcile failed");
    } finally { setBusy(false); }
  };

  const productRows = useMemo(() => {
    if (!result) return [];
    return result.stock_check.map((s) => ({
      sku: s.sku, name: s.name, on_hand: s.on_hand, sold: s.sold,
      implied_opening: s.implied_opening, revenue: s.revenue,
    }));
  }, [result]);

  const doImport = async () => {
    if (!result) return;
    setImporting(true); setImportResult(null);
    try {
      const products = result.new_products.map((p) => ({
        sku: p.sku, name: p.name,
        // pass along any extra fields the user has toggled on
        ...Object.fromEntries(Object.entries(p.source_row || {}).filter(([k]) => orphanCols.includes(k))),
      }));
      const clients = result.clients.map((c) => ({ ...c }));
      const { data } = await api.post("/reconcile/import", { products, clients });
      setImportResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Import failed");
    } finally { setImporting(false); }
  };

  return (
    <div className="p-6 lg:p-10 max-w-6xl" data-testid="reconcile-page">
      <header className="mb-6">
        <p className="label-eyebrow">Data ingest</p>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-1">CSV Reconcile</h1>
        <p className="text-sm text-gray-500 mt-2 max-w-2xl">
          Upload a <strong>product/inventory</strong> CSV and a <strong>transactions</strong> CSV. MPower! detects columns, cross-checks SKUs, balances stock, and extracts customers. Toggle fields on/off before you import.
        </p>
      </header>

      <Card className="p-5 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <FileDrop label="Items / Inventory CSV" file={itemsFile} onFile={setItemsFile} testid="items-drop" />
          <FileDrop label="Transactions CSV" file={txFile} onFile={setTxFile} testid="tx-drop" />
        </div>
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-gray-500">Empty fields are ignored. Unknown columns are auto-added — you can hide them below.</p>
          <Button onClick={runReconcile} disabled={busy || !itemsFile || !txFile} className="btn-brand" data-testid="run-reconcile-btn">
            <Sparkles size={16} className="mr-1" /> {busy ? "Reconciling…" : "Reconcile"}
          </Button>
        </div>
        {error && <div className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2" data-testid="reconcile-error">{error}</div>}
      </Card>

      {result && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Card className="p-4"><div className="label-eyebrow">Items rows</div><div className="text-2xl font-bold">{result.items.count}</div></Card>
            <Card className="p-4"><div className="label-eyebrow">Tx rows</div><div className="text-2xl font-bold">{result.transactions.count}</div></Card>
            <Card className="p-4 bg-emerald-50"><div className="label-eyebrow text-emerald-700">Matched</div><div className="text-2xl font-bold text-emerald-700">{result.matched_count}</div></Card>
            <Card className="p-4 bg-amber-50"><div className="label-eyebrow text-amber-700">Orphans</div><div className="text-2xl font-bold text-amber-700">{result.orphan_count}</div></Card>
          </div>

          {/* Products / Stock check */}
          <Card className="p-5 mb-5" data-testid="products-section">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><Package size={18} /><h2 className="font-bold text-lg">Products & stock</h2></div>
              <Button variant="outline" size="sm" onClick={() => download("products_reconciled.csv", toCSV(productRows, productCols))} data-testid="download-products-btn">
                <Download size={14} className="mr-1" /> CSV
              </Button>
            </div>
            <FieldToggles headers={["sku", "name", "on_hand", "sold", "implied_opening", "revenue"]} visible={productCols} setVisible={setProductCols} />
            <PreviewTable rows={productRows} headers={productCols} testid="products-preview" />
          </Card>

          {/* Orphan / new products */}
          {result.new_products.length > 0 && (
            <Card className="p-5 mb-5 border-amber-200" data-testid="orphans-section">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2"><AlertTriangle size={18} className="text-amber-600" /><h2 className="font-bold text-lg">New products found in transactions</h2></div>
                <Button variant="outline" size="sm" onClick={() => download("new_products.csv", toCSV(result.new_products.map((p) => ({ sku: p.sku, name: p.name, ...p.source_row })), ["sku", "name", ...orphanCols]))} data-testid="download-orphans-btn">
                  <Download size={14} className="mr-1" /> CSV
                </Button>
              </div>
              <p className="text-xs text-gray-500 mb-3">These SKUs sold but weren&apos;t in Items-Table. They&apos;ll be added to Products on import.</p>
              <FieldToggles headers={orphanCols} visible={orphanCols} setVisible={setOrphanCols} />
              <PreviewTable
                rows={result.new_products.map((p) => ({ sku: p.sku, name: p.name, ...p.source_row }))}
                headers={["sku", "name", ...orphanCols.filter((h) => h !== "sku" && h !== "name")]}
                testid="orphans-preview"
              />
            </Card>
          )}

          {/* Clients */}
          <Card className="p-5 mb-5" data-testid="clients-section">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><Users size={18} /><h2 className="font-bold text-lg">Extracted clients ({result.clients.length})</h2></div>
              <Button variant="outline" size="sm" onClick={() => download("clients_reconciled.csv", toCSV(result.clients, clientCols))} data-testid="download-clients-btn">
                <Download size={14} className="mr-1" /> CSV
              </Button>
            </div>
            <p className="text-xs text-gray-500 mb-3">Rows with <span className="font-semibold text-amber-700">flag_review = true</span> matched on more than one identifier — worth a quick look before import.</p>
            <FieldToggles headers={["name", "company", "email", "phone", "hits", "revenue", "flag_review"]} visible={clientCols} setVisible={setClientCols} />
            <PreviewTable rows={result.clients} headers={clientCols} testid="clients-preview" />
          </Card>

          {/* Raw items table (collapsible view via toggles) */}
          <Card className="p-5 mb-5" data-testid="items-raw-section">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><FileSpreadsheet size={18} /><h2 className="font-bold text-lg">Items CSV — raw</h2></div>
            </div>
            <FieldToggles headers={result.items.headers} visible={itemsCols} setVisible={setItemsCols} />
            <PreviewTable rows={result.items.rows} headers={itemsCols} testid="items-raw-preview" />
          </Card>

          {/* Import */}
          <Card className="p-5 border-[var(--brand)]" data-testid="import-section">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1"><CheckCircle2 size={18} className="text-[var(--brand)]" /><h2 className="font-bold text-lg">Import into MPower!</h2></div>
                <p className="text-sm text-gray-600">Adds <strong>{result.new_products.length}</strong> new product(s) and <strong>{result.clients.length}</strong> client(s). Existing SKUs / emails are skipped.</p>
              </div>
              <Button onClick={doImport} disabled={importing} className="btn-brand" data-testid="run-import-btn">
                {importing ? "Importing…" : "Import now"}
              </Button>
            </div>
            {importResult && (
              <div className="mt-4 text-sm bg-emerald-50 border border-emerald-200 rounded p-3" data-testid="import-result">
                <div className="font-semibold text-emerald-800">Done.</div>
                <div className="text-emerald-700 mt-1">
                  Created {importResult.products} product(s), {importResult.clients} client(s).
                  Skipped {importResult.skipped_products} product(s), {importResult.skipped_clients} client(s) (already existed or invalid).
                </div>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
